/**
 * Error handling utilities for user-friendly error messages.
 * In development, shows full error details. In production, logs full error
 * and returns user-friendly messages.
 */

import { apiConfig } from '../config/api'

const isDev = import.meta.env.DEV

/**
 * Formats an error for display to users.
 * In dev: returns full error details
 * In prod: logs full error and returns user-friendly message
 */
export function formatErrorForUser(error: unknown, context?: string): string {
  const errorMessage = error instanceof Error ? error.message : String(error)
  const errorStack = error instanceof Error ? error.stack : undefined
  
  // Log full error details (always, for debugging)
  if (context) {
    console.error(`[${context}]`, error)
  } else {
    console.error('Error:', error)
  }
  
  // In development, show full error details
  if (isDev) {
    return errorMessage
  }
  
  // In production, return user-friendly messages
  const lowerMessage = errorMessage.toLowerCase()
  
  // Network errors
  if (lowerMessage.includes('network') || 
      lowerMessage.includes('fetch') || 
      lowerMessage.includes('connection') ||
      lowerMessage.includes('timeout')) {
    return 'Connection error. Please check your internet connection and try again.'
  }
  
  // Stripe payment errors
  if (lowerMessage.includes('stripe') || 
      lowerMessage.includes('payment') ||
      lowerMessage.includes('card')) {
    if (lowerMessage.includes('declined') || lowerMessage.includes('insufficient')) {
      return 'Your payment was declined. Please check your card details or try a different payment method.'
    }
    if (lowerMessage.includes('expired')) {
      return 'Your card has expired. Please use a different payment method.'
    }
    if (lowerMessage.includes('cvc') || lowerMessage.includes('security')) {
      return 'Card verification failed. Please check your card details and try again.'
    }
    return 'Payment processing failed. Please try again or contact support if the issue persists.'
  }
  
  // Authentication errors
  if (lowerMessage.includes('auth') || 
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('token') ||
      lowerMessage.includes('permission')) {
    return 'Authentication error. Please sign in again and try again.'
  }
  
  // Server errors
  if (lowerMessage.includes('500') || 
      lowerMessage.includes('server error') ||
      lowerMessage.includes('internal error')) {
    return 'Server error. Please try again in a few moments. If the problem persists, contact support.'
  }
  
  // Not found errors
  if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
    return 'The requested resource was not found. Please refresh the page and try again.'
  }
  
  // Validation errors
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
    return 'Invalid input. Please check your information and try again.'
  }
  
  // Meta/Facebook API errors
  if (lowerMessage.includes('meta') || 
      lowerMessage.includes('facebook') ||
      lowerMessage.includes('ad account')) {
    if (lowerMessage.includes('overlap') || lowerMessage.includes('location')) {
      return 'Campaign targeting error. Our team has been notified and will fix this shortly.'
    }
    if (lowerMessage.includes('permission') || lowerMessage.includes('access')) {
      return 'Campaign creation error. Our team has been notified and will resolve this shortly.'
    }
    return 'Campaign creation failed. Our team has been notified and will help you complete setup.'
  }
  
  // Generic fallback
  return 'An error occurred. Please try again. If the problem persists, contact support.'
}

/**
 * Logs an error with context for debugging.
 * Always logs full details regardless of environment.
 */
export function logError(error: unknown, context?: string): void {
  const errorMessage = error instanceof Error ? error.message : String(error)
  const errorStack = error instanceof Error ? error.stack : undefined
  
  if (context) {
    console.error(`[${context}]`, {
      message: errorMessage,
      stack: errorStack,
      error
    })
  } else {
    console.error('Error:', {
      message: errorMessage,
      stack: errorStack,
      error
    })
  }
}

/**
 * Sends error notification to support team.
 * Only sends in production to avoid spam in development.
 * Returns a promise that resolves when notification is sent (or skipped).
 */
export async function notifySupport(
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  // Only send notifications in production
  if (isDev) {
    console.log('[Error Notification] Skipped in development mode', { context, error })
    return
  }

  try {
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorType = error instanceof Error ? error.constructor.name : typeof error
    
    // Determine if this is a critical error that should notify support
    const lowerMessage = errorMessage.toLowerCase()
    const isCritical = 
      lowerMessage.includes('meta') ||
      lowerMessage.includes('facebook') ||
      lowerMessage.includes('campaign') ||
      lowerMessage.includes('ad account') ||
      lowerMessage.includes('500') ||
      lowerMessage.includes('server error')
    
    if (!isCritical) {
      // Only notify for critical errors
      return
    }

    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      console.warn('[Error Notification] No auth token, skipping notification')
      return
    }

    // Extract campaign_id from metadata if available
    const campaignId = metadata?.campaign_id as string | undefined

    const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/error/notify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        error_type: errorType,
        error_message: errorMessage,
        context: context,
        campaign_id: campaignId,
        metadata: {
          ...metadata,
          stack: error instanceof Error ? error.stack : undefined,
          user_agent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      })
    })

    if (!response.ok) {
      console.warn('[Error Notification] Failed to send notification', response.status)
    } else {
      console.log('[Error Notification] Sent successfully', { context, errorType })
    }
  } catch (err) {
    // Don't throw - notification failure shouldn't break the app
    console.warn('[Error Notification] Exception while sending notification', err)
  }
}

